import { animate, style, transition, trigger } from '@angular/animations';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  OverlayRef,
} from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  output,
  viewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLinkActive, RouterModule } from '@angular/router';
import { convertMenuPositionToConnectedPositions } from 'app/core/models/menu/convert-menu-positions';
import { MenuItem } from 'app/core/models/menu/menu.model';
import { MenuService } from 'app/core/services/menu/menu.service';
import { BrowserStorageService } from '@core/utilities/browser-storage/browser-storage.service';

@Component({
  selector: 'sqep-menu-item',
  imports: [
    RouterModule,
    RouterLinkActive,
    MatTooltipModule,
    MatListModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
  ],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandContractMenu', [
      transition(':enter', [
        style({ opacity: 0, height: '0px' }),
        animate('500ms ease-in-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [
        animate('500ms ease-in-out', style({ opacity: 0, height: '0px' })),
      ]),
    ]),
  ],
})
export class MenuItemComponent implements AfterViewInit {
  private readonly homeMenu = inject(MenuService);
  private readonly storageService = inject(BrowserStorageService);
  private menuTriggers = viewChildren(MatMenuTrigger);
  private menuTrigger!: MatMenuTrigger;
  private timedOutCloser: ReturnType<typeof setTimeout>;
  private timeOut: number = 100;

  protected item = input.required<MenuItem>();
  protected collapsed = model.required<boolean>();
  protected selected = model<string>('');
  protected selectedMenuItem = output<string>();
  protected changeCollapsedState = output<boolean>();

  constructor() {
    this.homeMenu.homeMenu$.subscribe(() => {
      this.selectedItem('Home');
    });
  }

  ngAfterViewInit(): void {
    this.menuTriggers().forEach((item) => {
      item.menuOpened.pipe().subscribe(() => {
        this.recalculateMenu(item);
      });
    });
  }

  recalculateMenu(menuTrigger: MatMenuTrigger) {
    const overlayRef: OverlayRef = (menuTrigger as any)._overlayRef;
    const overlayConfig = overlayRef.getConfig();
    (overlayConfig.positionStrategy as any)._isInitialRender = true;
    this.setPosition(
      menuTrigger,
      overlayConfig.positionStrategy as FlexibleConnectedPositionStrategy,
    );
    overlayConfig.positionStrategy.apply();
  }

  setPosition(
    menuTrigger: MatMenuTrigger,
    positionStrategy: FlexibleConnectedPositionStrategy,
  ) {
    let positions: ConnectedPosition[];
    if (menuTrigger.triggersSubmenu()) {
      positions = convertMenuPositionToConnectedPositions(['right', 'top'], -8);
    } else {
      positions = convertMenuPositionToConnectedPositions(['right', 'top']);
    }
    positionStrategy.withPositions(positions);
  }

  selectedItem(item: string) {
    this.storageService.set('lastSelectedMenuItem', item);
    this.selected.set(item);
    this.selectedMenuItem.emit(item);
    this.changeCollapsedState.emit(true);
  }

  mouseEnter(trigger: MatMenuTrigger) {
    if (this.timedOutCloser) {
      clearTimeout(this.timedOutCloser);
    }

    trigger?.openMenu();
    this.menuTrigger = trigger;
  }

  mouseLeave(trigger: MatMenuTrigger) {
    this.timedOutCloser = setTimeout(() => {
      trigger?.closeMenu();
    }, this.timeOut);
  }
}
